import { createElement } from 'react';
import { ElementFactory, Question, Serializer, SurveyModel, SvgRegistry } from 'survey-core';
import { localization, SurveyCreator } from 'survey-creator-react';
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui';
import FunctionalQuestionRubricGrid from './Rubric';

const CUSTOM_TYPE = 'rubricgrid';

export class QuestionRubricGridModel extends Question {
  get columns() {
    return this.getPropertyValue('columns');
  }

  set columns(val) {
    this.setPropertyValue('columns', val);
  }

  get rows() {
    return this.getPropertyValue('rows');
  }

  set rows(val) {
    this.setPropertyValue('rows', val);
  }

  get allowMultipleValuesInRow() {
    return this.getPropertyValue('oneValuePerRow');
  }

  get bandedRows() {
    return this.getPropertyValue('bandedRows');
  }

  set bandedRows(val) {
    this.setPropertyValue('bandedRows', val);
  }

  get cellAlign() {
    return this.getPropertyValue('cellAlign');
  }

  set cellAlign(val) {
    this.setPropertyValue('cellAlign', val);
  }

  get cellVerticalAlign() {
    return this.getPropertyValue('cellVerticalAlign');
  }

  set cellVerticalAlign(val) {
    this.setPropertyValue('cellVerticalAlign', val);
  }

  get cellPadding() {
    return this.getPropertyValue('cellPadding');
  }

  set cellPadding(val) {
    this.setPropertyValue('cellPadding', val);
  }

  get headerAlign() {
    return this.getPropertyValue('headerAlign');
  }

  set headerAlign(val) {
    this.setPropertyValue('headerAlign', val);
  }

  get headerVerticalAlign() {
    return this.getPropertyValue('headerVerticalAlign');
  }

  set headerVerticalAlign(val) {
    this.setPropertyValue('headerVerticalAlign', val);
  }

  set allowMultipleValuesInRow(val) {
    this.setPropertyValue('oneValuePerRow', val);
  }

  getType(): string {
    return CUSTOM_TYPE;
  }
}

export function registerRubricGrid() {
  return ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
    return new QuestionRubricGridModel(name) as QuestionRubricGridModel;
  });
}

// Specify display names for the question type and its properties
const locale = localization.getLocale('');
locale.qt[CUSTOM_TYPE] = 'Rubric matrix';
locale.pe.columns = 'Columns';

// Register an SVG icon for the question type
const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M352 64l0 96 128 0 0-64c0-17.7-14.3-32-32-32l-96 0zm-32 0L192 64l0 96 128 0 0-96zM160 64L64 64C46.3 64 32 78.3 32 96l0 64 128 0 0-96zM32 192l0 128 128 0 0-128L32 192zm0 160l0 64c0 17.7 14.3 32 32 32l96 0 0-96L32 352zm160 96l128 0 0-96-128 0 0 96zm160 0l96 0c17.7 0 32-14.3 32-32l0-64-128 0 0 96zM480 320l0-128-128 0 0 128 128 0zM0 96C0 60.7 28.7 32 64 32l384 0c35.3 0 64 28.7 64 64l0 320c0 35.3-28.7 64-64 64L64 480c-35.3 0-64-28.7-64-64L0 96zM192 320l128 0 0-128-128 0 0 128z"/></svg>`;
SvgRegistry.registerIconFromSvg(CUSTOM_TYPE, svg);

const properties = [
  {
    name: 'columns:json',
    category: 'columns'
  },
  { name: 'rows:json', category: 'rows' },
  { name: 'oneValuePerRow:boolean', category: 'rows' },
  {
    name: 'bandedRows:boolean',
    category: 'rows'
  },
  {
    name: 'cellAlign',
    choices: ['left', 'center', 'right'],
    default: 'center',
    category: 'general'
  },
  {
    name: 'cellVerticalAlign',
    choices: ['top', 'middle', 'bottom'],
    default: 'middle',
    category: 'general'
  },
  { name: 'cellPadding:number', category: 'general' },
  {
    name: 'headerAlign',
    choices: ['left', 'center', 'right'],
    default: 'center',
    category: 'general'
  },
  {
    name: 'headerVerticalAlign',
    choices: ['top', 'middle', 'bottom'],
    default: 'middle',
    category: 'general'
  }
];

Serializer.addClass(
  CUSTOM_TYPE,
  properties,
  function () {
    return new QuestionRubricGridModel('');
  },
  'question'
);

Serializer.addProperties(CUSTOM_TYPE, properties);

class SurveyQuestionRubricGrid extends SurveyQuestionElementBase {
  survey: SurveyModel | SurveyCreator;
  constructor(props: {
    survey: SurveyModel | SurveyCreator;
    question: QuestionRubricGridModel;
    isDisplayMode: boolean;
  }) {
    super(props);
    this.survey = props.survey;
    this.state = { value: this.question.value };

    this.question.onPropertyChanged.add(() => {
      const value = JSON.parse(JSON.stringify(this.question.value || []));
      if (this._isMounted) {
        this.setState({ value });
      } else {
        this.state = { value };
      }
    });
  }

  private _isMounted = false;

  componentDidMount(): void {
    super.componentDidMount?.();
    this._isMounted = true;
  }

  componentWillUnmount(): void {
    super.componentWillUnmount?.();
    this._isMounted = false;
  }

  get question() {
    return this.questionBase;
  }

  get value() {
    return this.question.value;
  }

  get columns() {
    return this.question.columns;
  }

  get rows() {
    return this.question.rows || [];
  }

  get allowMultipleValuesInRow() {
    return this.question.allowMultipleValuesInRow;
  }

  get bandedRows() {
    return this.question.bandedRows;
  }

  get cellAlign() {
    return this.question.cellAlign;
  }

  get cellVerticalAlign() {
    return this.question.cellVerticalAlign;
  }

  get cellPadding() {
    return this.question.cellPadding;
  }

  get headerAlign() {
    return this.question.headerAlign;
  }

  get headerVerticalAlign() {
    return this.question.headerVerticalAlign;
  }

  // survey.state changes (e.g. showPreview / Edit) instead of a cached flag.
  get isReadOnly(): boolean {
    return this.isDisplayMode;
  }

  // Support the read-only and design modes
  get style() {
    return this.isReadOnly ? { pointerEvents: 'none' } : undefined;
  }

  renderElement() {
    const rubridgrid = createElement(FunctionalQuestionRubricGrid, {
      columns: this.columns,
      rows: this.rows,
      headerAlign: this.headerAlign,
      headerVerticalAlign: this.headerVerticalAlign,
      cellAlign: this.cellAlign,
      cellVerticalAlign: this.cellVerticalAlign,
      bandedRows: this.bandedRows,
      allowMultipleValuesInRow: this.allowMultipleValuesInRow,
      readOnly: this.isReadOnly,
      survey:
        typeof (this.survey as SurveyCreator)?.survey?.runExpression === 'function'
          ? this.survey?.survey
          : typeof (this.survey as SurveyModel)?.runExpression === 'function'
            ? this.survey
            : this.question.survey,
      // key: JSON.stringify(this.state.value), // Force re-mount on value change
      questionName: this.question.name,
      value: this.state.value, // <-- Use state, not this.question.value
      setValue: (val: unknown) => {
        this.question.value = val;
        this.setState({ value: val }); // <-- Ensure state updates
      }
    });

    return rubridgrid;
  }
}

export default ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createElement(SurveyQuestionRubricGrid, props as any)
);
